/**
 * Avatar Catalog — Level-gated commander avatar unlocks (Kategorie F,
 * docs/XP_PROGRESSION_PLAN.md section 6.1).
 *
 * Two tiers over the same active roster, ordered by rarity (Common first):
 * - 'sprite'  (levels 1..N):   the in-game procedural pixel sprite per unit
 *   (js/engine/render/unitSprites.js, buildSpriteBitmap) - zero new art.
 * - 'artwork' (levels N+1..2N): the same units again, now from their card
 *   splash art (js/data/cards.js, getCardImageUrl) - higher fidelity as a
 *   visible step up from tier 1.
 * A third 'bespoke' tier (custom-commissioned portraits) is intentionally
 * out of scope - see the plan doc.
 *
 * Pure content module: only imports CARDS_DATA, no engine/render imports,
 * so js/engine/progress.js can use it for equip-validation without pulling
 * in any rendering code.
 */

import { CARDS_DATA } from '../data/cards.js';

const RARITY_ORDER = { Common: 0, Uncommon: 1, Rare: 2, Epic: 3 };

let _rosterCache = null;

// Exact same active-roster filter as ShowroomModal.init() (js/ui/showroomModal.js)
// so the avatar catalog always matches what's actually selectable in the
// Arsenal's Units tab - see docs/INVARIANTS.md convention 6 (active roster
// vs. legacy archive).
function getOrderedRoster() {
  if (_rosterCache) return _rosterCache;
  _rosterCache = CARDS_DATA
    .filter(c => (c.enabled === true || c.type === 'Melt')
      && !c.isSpell && c.type !== 'Spell'
      && !c.isModule && c.type !== 'Module')
    .slice()
    .sort((a, b) => (RARITY_ORDER[a.rarity] ?? 9) - (RARITY_ORDER[b.rarity] ?? 9));
  return _rosterCache;
}

let _catalogCache = null;

export function getAvatarCatalog() {
  if (_catalogCache) return _catalogCache;
  const roster = getOrderedRoster();
  const catalog = [];

  roster.forEach((card, i) => {
    catalog.push({
      // Keyed by card NAME, not unitType - several cards share a unitType
      // (e.g. "Marine" and "Black Marine" both use unitType 'Marine' as a
      // reskin), which would otherwise collide into one catalog entry.
      id: `sprite:${card.name}`,
      tier: 'sprite',
      unlockLevel: i + 1,
      unitName: card.name,
      unitType: card.unitType || card.name,
      race: card.race || 'Human'
    });
  });

  roster.forEach((card, i) => {
    catalog.push({
      id: `artwork:${card.name}`,
      tier: 'artwork',
      unlockLevel: roster.length + i + 1,
      unitName: card.name,
      image: card.images?.[0] || null
    });
  });

  _catalogCache = catalog;
  return catalog;
}

export function getAvatarById(id) {
  return getAvatarCatalog().find(a => a.id === id) || null;
}

export function getUnlockedAvatars(level) {
  return getAvatarCatalog().filter(a => a.unlockLevel <= level);
}
